import {BaseStore, globalStore, StoreObject, type StoreClass} from "../../stem-core/state/Store";
import type {StoreId} from "../../stem-core/state/State";
import {Money} from "../../stem-core/localization/Money";
import {apiClient, type MerchantScopedRequest} from "../connection/BlinkApiClient";
import {StemDate} from "../../stem-core/time/Date";
import {Currency} from "../../stem-core/localization/CurrencyStore";
import {Merchant} from "./MerchantStore";
import {field} from "../../stem-core/state/StoreField";
import {CycleUnit} from "./RecurringPaymentStoreObject";
import {EditableObjectStatus, InputFieldRequiredType, VisibilityStatus} from "./misc/GenericEnums";
import {BlinkGlobal} from "../../blinkpay/UtilsLib";
import {filterStoreByMerchantAndLooseIdArray} from "./misc/Filter";
import {ShippingPriceEntry, type RawShippingPriceEntry} from "./misc/ShippingPrice";
import {SubscriptionCoverage} from "./SubscriptionCoverageStore";


@globalStore
export class BillingPlan extends BaseStore("BillingPlan", {dependencies: ["Currency", "Merchant"]}) {
    @field(Number) merchantId: number;
    @field("Merchant") merchant: Merchant;
    @field(Number) currencyId: number;
    @field("Currency") currency: Currency;
    @field(Number) maxPaymentAttemptsCount: number;
    @field(Number) paymentAttemptDelayHours: number;
    @field(Number) attemptsBeforeNextCycle: number;
    @field(Boolean) useCalendarPeriods: boolean;
    @field(Boolean) collectAddress: boolean;
    @field(Number) amount: number;
    @field(CycleUnit) cycleUnit: CycleUnit;
    @field(Number) cycleUnitCount: number;
    @field(Object) shippingPrices: RawShippingPriceEntry[]; // The first matching entry is the one used
    @field(Number) minNumberOfCycles: number;
    @field(Number) maxNumberOfCycles: number;
    @field(Boolean) autorenewEditable: boolean;
    @field(Boolean) autorenewDefault: boolean;
    @field(Boolean) collectBillingAddress: boolean;
    @field(Number) freeTrialDays: number;
    @field(InputFieldRequiredType) trialPaymentMethodRequired: InputFieldRequiredType;
    @field(EditableObjectStatus) status: EditableObjectStatus;
    @field(Array) extraBenefitIds: number[];

    // TODO @Mihai does this method really need to exist?
    hasShippingPrice(): boolean {
        // TODO: this is a strange method
        return Array.isArray(this.shippingPrices) && this.shippingPrices.length > 0;
    }

    // TODO @branch cleanup and merge with Product
    // Return an array of ShippingPrice objects. Will modify the objects inline.
    getShippingPrices(): ShippingPriceEntry[] {
        return ShippingPriceEntry.loadArray(this.shippingPrices, this.currencyId);
    }

    getAbbreviatedCycleDuration() {
        const cycleUnitAbrev = this.cycleUnit.getAbrev();
        return this.cycleUnitCount > 1 ? this.cycleUnitCount + " " + cycleUnitAbrev : cycleUnitAbrev;
    }

    getCycleDuration(omitCountOnSingular=false) {
        return this.cycleUnit.timeUnit.formatCount(this.cycleUnitCount, omitCountOnSingular);
    }

    getCurrency() {
        return this.currency;
    }

    formatPrice(amount: number): Money {
        return new Money(amount, this.getCurrency());
    }

    // TODO @branch Replace usage with getBasePrice
    getPrice(): Money {
        return this.formatPrice(this.amount);
    }

    getBasePrice() {
        return this.formatPrice(this.amount);
    }

    formatBasePrice() {
        return `${this.getBasePrice()}/${this.getCycleDuration(true)}`;
    }

    serializeForSDK() {
        const {amount, collectBillingAddress, currencyId, cycleUnit, cycleUnitCount, id, merchantId, shippingPrices, status} = this;
        return {amount, collectBillingAddress, currencyId, cycleUnit, cycleUnitCount, id, merchantId, shippingPrices, status};
    }
}


@globalStore
export class SubscriptionOffer extends BaseStore("SubscriptionOffer", {dependencies: ["SubscriptionCoverage", "BillingPlan"]}) {
    // Set by MerchantSubscriptionsPage from the offers summary, not sent by the server
    declare currentSubscribersCount?: number;
    declare allTimeSubscribersCount?: number;

    // Newest offer first.
    static comparator = (a: SubscriptionOffer, b: SubscriptionOffer) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime();

    @field("Merchant") merchant: Merchant;
    @field("BillingPlan") billingPlan: BillingPlan;
    @field(Number) coverageId: number;
    @field("SubscriptionCoverage") coverage: SubscriptionCoverage;
    @field(VisibilityStatus) visibility: VisibilityStatus;
    @field(Array) extraBenefitIds: number[];
    @field(Boolean) collectAddress: boolean;
    @field(Date) startDate: StemDate;
    @field(Date) endDate: StemDate;
    @field(Date) deletedAt: StemDate;
    @field(Boolean) revoked: boolean;
    @field(String) title?: string;
    @field(String) description?: string
    @field(String) alias?: string;

    toString(): string {
        return `${this.getName()} ${this.formatBasePrice()}`
    }

    getCoverage() {
        return this.coverage;
    }

    shouldCollectAddress() {
        return this.coverage.requiresAddress || this.collectAddress;
    }

    getMerchant() {
        return this.merchant;
    }

    hasShippingPrice() {
        return this.billingPlan.hasShippingPrice();
    }

    // Return an array of ShippingPrice objects. Will modify the objects inline.
    getShippingPrices() {
        return this.billingPlan.getShippingPrices();
    }

    getName() {
        return this.title || this.getCoverage()?.getName();
    }

    getAbbreviatedCycleDuration() {
        return this.billingPlan.getAbbreviatedCycleDuration();
    }

    getCycleDuration(omitCountOnSingular=false) {
        return this.billingPlan.getCycleDuration(omitCountOnSingular);
    }

    getCurrency() {
        return this.billingPlan.currency;
    }

    formatPrice(amount: number) {
        return this.billingPlan.formatPrice(amount);
    }

    // TODO @branch Go through usage of this and basePrice, and make sure they are correctly used
    // TODO: this should be called get localized price?
    getPrice() {
        // TODO: if pricing should include shipping should be a merchant setting
        const defaultPrice = this.billingPlan.getPrice();
        const defaultLocation = BlinkGlobal.userService?.userData?.location;
        if (!defaultLocation) {
            return defaultPrice;
        }

        const shippingPrices = this.getShippingPrices();

        for (const shippingPrice of shippingPrices) {
            if (shippingPrice.matchesAddress(defaultLocation)) {
                return defaultPrice.add(shippingPrice.getPrice());
            }
        }

        return defaultPrice;
    }

    getBasePrice() {
        return this.billingPlan.getBasePrice();
    }

    formatBasePrice() {
        return this.billingPlan.formatBasePrice();
    }

    getDescription() {
        return this.description || "";
    }

    getAllBenefitIds() {
        const benefitIds = new Set([...this.extraBenefitIds, ...this.coverage.benefitIds]);
        return Array.from(benefitIds);
    }

    // TODO @branch maybe rename to isActive?
    isAvailable() {
        return !this.revoked && !this.deletedAt && StemDate.now().getTime() >= (new StemDate(this.startDate)).getTime()
            && (!this.endDate || StemDate.now().getTime() <= (new StemDate(this.endDate)).getTime());
    }

    static allUnfiltered(): SubscriptionOffer[] {
        // Because the IFrameState is able to overwrite the set of subscription offers, we overwrite the .all() method
        // on this store to take that into account. We need this method to be able to implement the filter in
        // IFrameState.
        return super.all<SubscriptionOffer>();
    }

    // TODO @cleanup rewrite this
    static all<T extends StoreObject>(this: StoreClass<T>): T[] {
        return filterStoreByMerchantAndLooseIdArray(SubscriptionOffer.allUnfiltered(), BlinkGlobal.iFrameState?.subscriptionOffer,
            (offer, idOrAlias) => offer.id == idOrAlias || offer.alias == idOrAlias) as any;
    }
}

// One offer's price, as both endpoints below return it
// (recurring_payments/models/subscription.py SubscriptionPriceBreakdown).
export interface SubscriptionPriceBreakdown {
    recurringOfferId: number | null;
    currencyId: number;
    initialOfferPrice: number;
    afterTaxesPrice: number;
    finalPrice: number;
    discountedBeforeTaxesPrice: number;
    discountType: string | null;
    discountValue: number | string | null;
    deductedAmount: number;
    applyPolicy: string; // SubscriptionApplyPolicy
    coverageChangeType: string | null; // "upgrade" | "downgrade" | "equivalent"
    paidCycles: number;
    paidUntil: string | null;
}

export interface CalculatedSubscriptionPrices {
    recurringOffers: SubscriptionPriceBreakdown[];
}

// TODO @cleanup these endpoints should be merged
export async function apiCalculateSubscriptionOffersPrice(calculatePriceRequest: {
    discountCode?: string | null;
    merchantId?: StoreId | null;
    deliveryAddressId?: StoreId | null;
} = {
    discountCode: null,
    merchantId: null,
    deliveryAddressId: null,
}) {
    return apiClient.get<CalculatedSubscriptionPrices>("/subscriptions/calculate_subscription_price", calculatePriceRequest);
}

export async function apiCalculateSubscriptionEditOffersPrice(calculatePriceRequest: {
    subscriptionId?: StoreId | null;
    discountCode?: string | null;
    deliveryAddressId?: StoreId | null;
}={
    subscriptionId: null,
    discountCode: null,
    deliveryAddressId: null,
}) {
    return apiClient.get<CalculatedSubscriptionPrices>("/subscriptions/calculate_update_subscription_price", calculatePriceRequest);
}

// CreateSubscriptionOfferRequest (api/merchants/views.py)
export interface CreateSubscriptionOfferRequest extends MerchantScopedRequest {
    coverageId: StoreId;
    billingPlanId: StoreId;
    collectAddress: boolean;
    startDate: StemDate;
    visibility: VisibilityStatus;
    title?: string;
    description?: string;
    alias?: string;
    endDate?: StemDate;
}

export async function apiMerchantCreateSubscriptionOffer(request: CreateSubscriptionOfferRequest): Promise<SubscriptionOffer> {
    const response = await apiClient.post("/merchant/create_subscription_offer", request);
    return SubscriptionOffer.loadObject(response);
}

// EditSubscriptionOfferRequest. Everything but the id is optional and left out when unchanged; the call
// does not pass allowNulls, so a field is either sent with a value or not sent
export interface EditSubscriptionOfferRequest extends MerchantScopedRequest {
    subscriptionOfferId: StoreId;
    coverageId?: StoreId;
    billingPlanId?: StoreId;
    collectAddress?: boolean;
    title?: string;
    description?: string;
    alias?: string;
    startDate?: StemDate;
    endDate?: StemDate;
    visibility?: VisibilityStatus;
    extraBenefitIds?: StoreId[];
}

export async function apiMerchantEditSubscriptionOffer(request: EditSubscriptionOfferRequest): Promise<any> {
    return apiClient.post("/merchant/edit_subscription_offer", request);
}

// DeleteSubscriptionOfferRequest, the id on its own
export interface DeleteSubscriptionOfferRequest extends MerchantScopedRequest {
    subscriptionOfferId: StoreId;
}

export async function apiMerchantDeleteSubscriptionOffer(request: DeleteSubscriptionOfferRequest): Promise<any> {
    return apiClient.post("/merchant/delete_subscription_offer", request);
}

// CreateBillingPlanRequest
export interface CreateBillingPlanRequest extends MerchantScopedRequest {
    amount: number;
    oneTimeFee: number;
    cycleUnit: CycleUnit;
    cycleUnitCount: number;
    minNumberOfCycles: number;
    freeTrialDays: number;
    trialPaymentMethodRequired: InputFieldRequiredType;
    autorenewEditable: boolean;
    autorenewDefault: boolean;
    collectBillingAddress: boolean;
    status: EditableObjectStatus;
    maxNumberOfCycles?: number;
    autoconvertToId?: StoreId;
    shippingPrices?: ShippingPriceEntry[];
}

export async function apiMerchantCreateBillingPlan(request: CreateBillingPlanRequest): Promise<BillingPlan> {
    const response = await apiClient.post("/merchant/create_billing_plan", request);
    return BillingPlan.loadObject(response);
}

// What EditBillingPlanRequest validates (merchants/deserializers.py:135): the create fields, each optional.
// The backend only lets status move draft to anything, anything to inactive, or inactive back to active
export interface EditBillingPlanRequest extends Partial<CreateBillingPlanRequest> {
    billingPlanId: StoreId;
}

export function apiMerchantEditBillingPlan(request: EditBillingPlanRequest): Promise<any> {
    return apiClient.post("/merchant/edit_billing_plan", request);
}
